import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LOCALES, isUnlocalizedPath } from '@/i18n/config';
import { stripLocalePrefix, localizedPath } from '@/lib/localizedPath';

interface LanguageSwitcherProps {
  className?: string;
}

const LOCALE_COOKIE = 'locale';
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

/**
 * Locale picker. Always renders (even on the blog, which stays English-only)
 * so a visitor can jump to a translated page from wherever they are; picking
 * a locale while on the blog lands on the localized homepage instead, since
 * the blog itself has no per-locale URL.
 *
 * A manual pick always overwrites the `locale` cookie (unlike the passive,
 * set-once-if-absent write in src/i18n/index.ts) - it's an explicit
 * override of whatever middleware.ts's geo-redirect guessed, and it's
 * what middleware.ts reads on future visits, so it needs to stick.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { path: basePath } = stripLocalePrefix(location.pathname);
  const targetPath = isUnlocalizedPath(basePath) ? '/' : basePath;

  return (
    <select
      aria-label="Language"
      value={i18n.language}
      onChange={(e) => {
        const locale = e.target.value;
        document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
        window.location.href = localizedPath(targetPath, locale);
      }}
      className={`bg-transparent text-foreground/60 hover:text-foreground focus:outline-none cursor-pointer ${className}`}
    >
      {LOCALES.map((locale) => (
        <option key={locale.code} value={locale.code}>
          {locale.label}
        </option>
      ))}
    </select>
  );
};

export default LanguageSwitcher;
